/**
 * Types for Navigation, Pathfinding and Picking operations
 */

import { Position, Dimensions, ElementType } from './types';

// ============================================
// Navigation Graph Types
// ============================================

export type NavigationNodeType = 'ENTRY' | 'INTERSECTION' | 'RACK_ACCESS' | 'ZONE_ENTRY';

export interface NavigationNode {
  id: number;
  code: string;
  name?: string;
  nodeType: NavigationNodeType;
  position: Position;
  connectedLocationId?: number;
  connectedLocationCode?: string;
  isActive?: boolean;
}

export type EdgeType = 'BOTH' | 'FORKLIFT' | 'PEDESTRIAN';
export type TransportMode = 'forklift' | 'pedestrian';

export interface NavigationEdge {
  id: number;
  fromNodeId: number;
  toNodeId: number;
  fromNodeCode: string;
  toNodeCode: string;
  distance: number;
  isBidirectional: boolean;
  edgeType: EdgeType;
}

export interface NavigationGraph {
  warehouseId: number;
  warehouseName: string;
  nodes: NavigationNode[];
  edges: NavigationEdge[];
}

// ============================================
// Rack Detail (3D Cross-Section) Types
// ============================================

export type HeightAccessibility = 'GROUND' | 'EASY' | 'NORMAL' | 'LADDER' | 'EQUIPMENT';

export interface ShelfProduct {
  stockId: number;
  productId: number;
  productCode: string;
  productName: string;
  quantity: number;
  availableQuantity: number;
  batchNumber?: string;
  expiryDate?: string;
}

export interface ShelfSlot {
  slotIndex: number;
  locationId: number;
  locationCode: string;
  products: ShelfProduct[];
}

export interface ShelfInfo {
  level: number;
  heightCm: number;
  heightAccessibility: HeightAccessibility;
  slots: ShelfSlot[];
}

export interface RackAccessNode {
  id: number;
  code: string;
  position: Position;
}

export interface RackDetail {
  id: number;
  code: string;
  name: string;
  locationType: ElementType;
  position: Position;
  width: number;
  height: number;
  rotation: number;
  color: string;
  maxItems?: number;
  shelves: ShelfInfo[];
  accessNode?: RackAccessNode;
}

// ============================================
// Picking Types
// ============================================

export type PickingStrategy = 
  | 'shortest' 
  | 'shortest_3d' 
  | 'fifo' 
  | 'expiry_first' 
  | 'rack_grouped';

export interface PickingStepLocation {
  id: number;
  code: string;
  name: string;
  type: ElementType;
  position: Position;
  shelfLevel: number;
  shelfHeightCm: number;
  slotIndex: number;
}

export interface PickingStepRack {
  id: number;
  code: string;
  name: string;
}

export interface PickingStepProduct {
  id: number;
  code: string;
  name: string;
}

export interface PickingStep {
  order: number;
  location: PickingStepLocation;
  rack?: PickingStepRack;
  product: PickingStepProduct;
  quantityToPick: number;
  availableQuantity: number;
  batchNumber?: string;
  expiryDate?: string;
  heightAccessibility: HeightAccessibility;
  heightPenalty: number;
  navigationNodeId?: number;
}

export interface UnfulfilledItem {
  productId: number;
  productCode: string;
  productName: string;
  quantityNeeded: number;
  quantityMissing: number;
}

export interface PickingPath {
  steps: PickingStep[];
  strategy: PickingStrategy;
  totalDistance: number;
  totalHeightPenalties: number;
  estimatedTimeSeconds: number;
  pathCoordinates: Position[];
  isComplete: boolean;
  unfulfilledItems: UnfulfilledItem[];
}

// ============================================
// Product Location Types
// ============================================

export interface ProductLocationInfo {
  id: number;
  code: string;
  name: string;
  type: ElementType;
  position: Position;
  shelfLevel: number;
  shelfHeightCm: number;
  slotIndex: number;
  heightAccessibility: HeightAccessibility;
}

export interface ProductLocationRack {
  id: number;
  code: string;
  name: string;
}

export type ExpiryStatus = 'none' | 'ok' | 'warning' | 'expired';

export interface ProductLocationEntry {
  stockId: number;
  location: ProductLocationInfo;
  rack?: ProductLocationRack;
  quantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  batchNumber?: string;
  expiryDate?: string;
  daysToExpiry?: number | null;
  expiryStatus?: ExpiryStatus;
  stockedAt: string;
}

export interface ProductLocationsDetail {
  productId: number;
  warehouseId: number;
  warehouseName: string;
  totalQuantity: number;
  totalAvailable: number;
  totalReserved: number;
  locationsCount: number;
  locations: ProductLocationEntry[];
}

// ============================================
// Map Import Types
// ============================================

export interface ImportedShelf {
  level: number;
  heightCm: number;
  slots: number;
}

export interface ImportedElement {
  type: ElementType;
  code: string;
  name?: string;
  position: Position;
  dimensions: Dimensions;
  rotation?: number;
  color?: string;
  shelves?: ImportedShelf[];
}

export interface ImportedNavigationNode {
  id: string;
  code?: string;
  name?: string;
  type: NavigationNodeType;
  position: Position;
  connectedRackCode?: string;
}

export interface ImportedNavigationEdge {
  from: string;
  to: string;
  bidirectional?: boolean;
}

export interface ImportedNavigation {
  nodes: ImportedNavigationNode[];
  edges: ImportedNavigationEdge[];
}

export interface MapImportData {
  name?: string;
  description?: string;
  dimensions: {
    width: number;
    height: number;
  };
  elements: ImportedElement[];
  navigation?: ImportedNavigation;
  startPoint?: {
    x: number;
    y: number;
    name?: string;
  };
  sampleProducts?: any[];
}

// ============================================
// API Request/Response Types
// ============================================

export interface CalculatePickingPathRequest {
  documentId?: number;
  items?: Array<{
    productId: number;
    quantity: number;
  }>;
  strategy: PickingStrategy;
  startNodeId?: number;
  transportMode?: TransportMode;
}

export interface CreateNavigationNodesRequest {
  nodes: Array<{
    id?: number | string;
    code: string;
    name?: string;
    nodeType: NavigationNodeType;
    positionX: number;
    positionY: number;
    connectedLocationId?: number;
    isActive?: boolean;
  }>;
}

export interface CreateNavigationEdgesRequest {
  edges: Array<{
    fromNodeId: number;
    toNodeId: number;
    isBidirectional?: boolean;
    edgeType?: EdgeType;
    associatedAisleId?: number;
  }>;
}

// ============================================
// UI State Types
// ============================================

export interface PathVisualizationState {
  isVisible: boolean;
  pickingPath?: PickingPath;
  highlightedStepIndex?: number;
  animationProgress: number;
}

export interface NavigationEditorState {
  isEditing: boolean;
  selectedNodeId?: number;
  selectedEdgeId?: number;
  tempNodes: NavigationNode[];
  tempEdges: NavigationEdge[];
  connectingFromNodeId?: number;
}

export interface RackDetailViewState {
  isOpen: boolean;
  rack?: RackDetail;
  highlightedShelfLevel?: number;
  highlightedSlotIndex?: number;
  highlightedProductId?: number;
}

// ============================================
// Helper Functions
// ============================================

export const getHeightAccessibilityLabel = (accessibility: HeightAccessibility): string => {
  const labels: Record<HeightAccessibility, string> = {
    GROUND: 'Poziom podłogi',
    EASY: 'Łatwy dostęp',
    NORMAL: 'Normalny dostęp',
    LADDER: 'Wymaga drabiny',
    EQUIPMENT: 'Wymaga sprzętu',
  };
  return labels[accessibility] || accessibility;
};

export const getHeightAccessibilityColor = (accessibility: HeightAccessibility): string => {
  const colors: Record<HeightAccessibility, string> = {
    GROUND: '#ffc107',   // Yellow - bending down
    EASY: '#4caf50',     // Green - optimal
    NORMAL: '#8bc34a',   // Light green - good
    LADDER: '#ff9800',   // Orange - needs ladder
    EQUIPMENT: '#f44336', // Red - needs equipment
  };
  return colors[accessibility] || '#9e9e9e';
};

export const getStrategyLabel = (strategy: PickingStrategy): string => {
  const labels: Record<PickingStrategy, string> = {
    shortest: 'Najkrótsza ścieżka',
    shortest_3d: 'Najkrótsza 3D',
    fifo: 'FIFO (najstarsze najpierw)',
    expiry_first: 'Najkrótsze daty ważności',
    rack_grouped: 'Grupuj po regałach',
  };
  return labels[strategy] || strategy;
};

export const formatEstimatedTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)} sek`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (remainingSeconds === 0) {
    return `${minutes} min`;
  }
  return `${minutes} min ${remainingSeconds} sek`;
};

export const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${Math.round(distance * 100)} cm`;
  }
  return `${distance.toFixed(1)} m`;
};
